import { useState, type ReactNode } from 'react';
import { GlobeAmericasIcon, BeakerIcon, UserGroupIcon, TagIcon } from '@heroicons/react/24/outline';
import type { EvidenceDistributionMetrics } from '@/utils/evidenceDistributionMetrics';
import { EvidenceDistributionCard, type CardAccent } from './EvidenceDistributionCard';

const CATEGORY_ACCENTS: Record<string, CardAccent> = {
  ancestry: {
    bar: '#6366f1',
    bg: '#e0e7ff',
    text: '#4338ca',
    badgeBg: '#eef2ff',
    badgeText: '#4f46e5',
  },
  diseaseModel: {
    bar: '#0d9488',
    bg: '#ccfbf1',
    text: '#0f766e',
    badgeBg: '#f0fdfa',
    badgeText: '#0f766e',
  },
  sex: {
    bar: '#de1872',
    bg: '#fce7f3',
    text: '#be185d',
    badgeBg: '#fdf2f8',
    badgeText: '#be185d',
  },
  associationType: {
    bar: '#2563eb',
    bg: '#dbeafe',
    text: '#1d4ed8',
    badgeBg: '#eff6ff',
    badgeText: '#2563eb',
  },
};

export type EvidenceDistributionGridProps = {
  metrics: EvidenceDistributionMetrics | null | undefined;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  badge?: string;
  className?: string;
  belowSexContent?: ReactNode;
  onFilterClick?: (
    category: 'ancestry' | 'disease_model' | 'sex' | 'association_type',
    label: string,
  ) => void;
};

export function EvidenceDistributionGrid({
  metrics,
  isLoading = false,
  title,
  subtitle,
  badge,
  className = '',
  belowSexContent,
  onFilterClick,
}: EvidenceDistributionGridProps) {
  const [cardsExpanded, setCardsExpanded] = useState(false);

  if (isLoading) {
    return (
      <EvidenceGridSkeleton
        title={title}
        subtitle={subtitle}
        className={className}
        belowSexContent={belowSexContent}
      />
    );
  }

  if (!metrics || metrics.totalRecords === 0) {
    return (
      <div className={`rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm ${className}`}>
        <p className="text-sm font-medium text-slate-600">No evidence records available</p>
        <p className="mt-1 text-xs text-slate-400">
          Evidence records were not found for the selected entity or association.
        </p>
      </div>
    );
  }

  const ancestryCard = (
    <EvidenceDistributionCard
      distribution={metrics.ancestry}
      accent={CATEGORY_ACCENTS.ancestry}
      icon={<GlobeAmericasIcon className="h-4 w-4" />}
      expanded={cardsExpanded}
      onExpandedChange={setCardsExpanded}
      onItemClick={onFilterClick ? label => onFilterClick('ancestry', label) : undefined}
    />
  );
  const diseaseModelCard = (
    <EvidenceDistributionCard
      distribution={metrics.diseaseModel}
      accent={CATEGORY_ACCENTS.diseaseModel}
      icon={<BeakerIcon className="h-4 w-4" />}
      expanded={cardsExpanded}
      onExpandedChange={setCardsExpanded}
      onItemClick={onFilterClick ? label => onFilterClick('disease_model', label) : undefined}
    />
  );
  const sexCard = (
    <EvidenceDistributionCard
      distribution={metrics.sex}
      accent={CATEGORY_ACCENTS.sex}
      icon={<UserGroupIcon className={belowSexContent != null ? 'h-3.5 w-3.5' : 'h-4 w-4'} />}
      compact={belowSexContent != null}
      maxInitialItems={belowSexContent != null ? Number.POSITIVE_INFINITY : 5}
      expanded={belowSexContent != null ? false : cardsExpanded}
      onExpandedChange={belowSexContent != null ? undefined : setCardsExpanded}
      onItemClick={onFilterClick ? label => onFilterClick('sex', label) : undefined}
    />
  );
  const associationTypeCard = (
    <EvidenceDistributionCard
      distribution={metrics.associationType}
      accent={CATEGORY_ACCENTS.associationType}
      icon={<TagIcon className="h-4 w-4" />}
      expanded={cardsExpanded}
      onExpandedChange={setCardsExpanded}
      onItemClick={onFilterClick ? label => onFilterClick('association_type', label) : undefined}
    />
  );

  return (
    <div className={`space-y-3.5 ${className}`}>
      {(title || subtitle || badge) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            {title && <h3 className="text-sm font-semibold text-slate-800">{title}</h3>}
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
          {badge && (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
              {badge}
            </span>
          )}
        </div>
      )}

      {belowSexContent != null ? (
        <div className="grid grid-cols-1 items-start gap-3.5 md:grid-cols-2 xl:grid-cols-4">
          <div className="h-full min-h-0 self-stretch">{ancestryCard}</div>
          <div className="h-full min-h-0 self-stretch">{diseaseModelCard}</div>
          <div className="h-full min-h-0 self-stretch">{associationTypeCard}</div>
          <div className="relative min-h-0 self-stretch" data-testid="evidence-sex-polarity-column">
            <div className="sticky top-0 z-10 grid min-w-0 gap-3">
              <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-full h-3 bg-white" />
              <div className="relative min-h-0">{sexCard}</div>
              <div className="relative min-h-0">{belowSexContent}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 items-stretch gap-3.5 md:grid-cols-2 xl:grid-cols-4">
          {ancestryCard}
          {diseaseModelCard}
          {sexCard}
          {associationTypeCard}
        </div>
      )}
    </div>
  );
}

function EvidenceGridSkeleton({
  title,
  subtitle,
  className,
  belowSexContent,
}: {
  title?: string;
  subtitle?: string;
  className?: string;
  belowSexContent?: ReactNode;
}) {
  const stackSexColumn = belowSexContent != null;
  const primaryCards = stackSexColumn
    ? (['Ancestry', 'Disease model', 'Association type'] as const)
    : (['Ancestry', 'Disease model', 'Sex / Gender', 'Association type'] as const);
  const primaryAccentKeys = stackSexColumn
    ? (['ancestry', 'diseaseModel', 'associationType'] as const)
    : (['ancestry', 'diseaseModel', 'sex', 'associationType'] as const);
  const rowWidths = ['92%', '78%', '64%', '48%', '36%'];

  return (
    <div
      className={`space-y-3.5 ${className}`}
      data-testid="evidence-distribution-skeleton"
      aria-busy="true"
      aria-label="Loading evidence profile"
    >
      {(title || subtitle) && (
        <div className="space-y-1.5">
          {title && <div className="h-4 w-44 animate-pulse rounded bg-slate-200" />}
          {subtitle && <div className="h-3 w-64 animate-pulse rounded bg-slate-100" />}
        </div>
      )}
      <div className="grid grid-cols-1 items-start gap-3.5 md:grid-cols-2 xl:grid-cols-4">
        {primaryCards.map((cardTitle, cardIndex) => {
          const accent = CATEGORY_ACCENTS[primaryAccentKeys[cardIndex]];
          return (
            <div key={cardTitle} className="h-full min-h-0 self-stretch">
              <SkeletonCard title={cardTitle} accent={accent} rowWidths={rowWidths} />
            </div>
          );
        })}
        {stackSexColumn && (
          <div className="relative min-h-0 self-stretch" data-testid="evidence-sex-polarity-column">
            <div className="sticky top-0 z-10 grid min-w-0 gap-3">
              <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-full h-3 bg-white" />
              <div className="relative min-h-0">
                <SkeletonCard
                  title="Sex / Gender"
                  accent={CATEGORY_ACCENTS.sex}
                  rowWidths={['78%', '64%', '48%']}
                  compact
                />
              </div>
              <div className="relative min-h-0">{belowSexContent}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SkeletonCard({
  title,
  accent,
  rowWidths,
  compact = false,
}: {
  title: string;
  accent: CardAccent;
  rowWidths: string[];
  compact?: boolean;
}) {
  return (
    <div
      className={`flex h-full flex-col rounded-xl border border-slate-200 bg-white shadow-sm ${
        compact ? 'p-2.5' : 'p-3.5'
      }`}
    >
      <div
        className={`flex items-center justify-between gap-2 border-b border-slate-100 ${
          compact ? 'mb-2 pb-2' : 'mb-3 pb-2.5'
        }`}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`flex shrink-0 animate-pulse items-center justify-center rounded-lg ${
              compact ? 'h-6 w-6' : 'h-7 w-7'
            }`}
            style={{ backgroundColor: accent.bg }}
          />
          <span className="truncate text-sm font-semibold uppercase tracking-wider text-slate-400">
            {title}
          </span>
        </div>
        <span className="h-5 w-16 animate-pulse rounded-full bg-slate-100" />
      </div>
      <div className={`flex-1 ${compact ? 'space-y-2' : 'space-y-3'}`}>
        {rowWidths.map((width, rowIndex) => (
          <div key={`${title}-${rowIndex}`} className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span
                className="h-3 animate-pulse rounded bg-slate-200"
                style={{ width: `${48 - rowIndex * 4}%` }}
              />
              <span className="h-3 w-10 shrink-0 animate-pulse rounded bg-slate-100" />
            </div>
            <div className={`w-full overflow-hidden rounded-full bg-slate-100 ${compact ? 'h-1' : 'h-1.5'}`}>
              <div
                className="h-full animate-pulse rounded-full"
                style={{ width, backgroundColor: accent.bg }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
